import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { tiktokAPI } from "../lib/api";
import { Loader2, ExternalLink, CheckCircle2, XCircle, Upload } from "lucide-react";

// TikTok Logo Component
const TikTokLogo = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

export function TikTokShareDialog({ video, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [tiktokStatus, setTiktokStatus] = useState(null);
  const [caption, setCaption] = useState("");
  const [privacyLevel, setPrivacyLevel] = useState("PUBLIC_TO_EVERYONE");
  const [publishToDrafts, setPublishToDrafts] = useState(false);
  const [publishStatus, setPublishStatus] = useState(null);

  // Check TikTok connection status when dialog opens
  useEffect(() => {
    if (open) {
      checkTikTokStatus();
      // Set default caption from video title
      if (video) {
        const hashtags = video.hashtags?.slice(0, 5).join(" ") || "";
        setCaption(`${video.title || ""} ${hashtags}`.trim());
      }
    }
  }, [open, video]);

  // Listen for OAuth callback messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === "tiktok-oauth-success") {
        toast.success(`Connected to TikTok as ${event.data.display_name}!`);
        setConnecting(false);
        checkTikTokStatus();
      } else if (event.data.type === "tiktok-oauth-error") {
        toast.error(`TikTok connection failed: ${event.data.message}`);
        setConnecting(false);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const checkTikTokStatus = async () => {
    try {
      const response = await tiktokAPI.getOAuthStatus();
      setTiktokStatus(response.data);
    } catch (error) {
      console.error("Failed to check TikTok status:", error);
      setTiktokStatus({ connected: false });
    }
  };

  const handleConnectTikTok = async () => {
    setConnecting(true);
    try {
      const response = await tiktokAPI.getOAuthLoginUrl();
      const authUrl = response.data.auth_url;
      
      // Open TikTok OAuth in popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      window.open(
        authUrl,
        "TikTok Login",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );
    } catch (error) {
      console.error("Failed to get TikTok auth URL:", error);
      toast.error("Failed to connect to TikTok. Please try again.");
      setConnecting(false);
    }
  };

  const handlePublish = async () => {
    if (!video) return;
    
    setLoading(true);
    setPublishStatus("processing");
    
    try {
      await tiktokAPI.publish({
        video_id: video.id,
        caption,
        privacy_level: privacyLevel,
        publish_to_drafts: publishToDrafts,
      });
      
      toast.success(
        publishToDrafts 
          ? "Video sent to TikTok drafts! Open TikTok to complete posting."
          : "Video is being uploaded to TikTok!"
      );
      setPublishStatus("success");
      
      // Close dialog after short delay
      setTimeout(() => {
        onOpenChange(false);
        setPublishStatus(null);
      }, 2000);
      
    } catch (error) {
      console.error("Failed to publish to TikTok:", error);
      toast.error(error.response?.data?.detail || "Failed to publish to TikTok");
      setPublishStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!tiktokStatus?.account?.id) return;
    
    try {
      await tiktokAPI.disconnectOAuth(tiktokStatus.account.id);
      toast.success("TikTok account disconnected");
      setTiktokStatus({ connected: false });
    } catch (error) {
      toast.error("Failed to disconnect TikTok account");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-gray-900 border-gray-800">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <TikTokLogo className="w-6 h-6" />
            Share to TikTok
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {tiktokStatus?.connected 
              ? `Post "${video?.title || "your video"}" to your TikTok account`
              : "Connect your TikTok account to share videos directly"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Connection Status */}
          {!tiktokStatus?.connected ? (
            <div className="flex flex-col items-center justify-center py-6 space-y-4">
              <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center">
                <TikTokLogo className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-400 text-center">
                Connect your TikTok account to share videos directly from your library
              </p>
              <Button
                onClick={handleConnectTikTok}
                disabled={connecting}
                className="bg-black hover:bg-gray-800 text-white border border-gray-700"
              >
                {connecting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <TikTokLogo className="w-4 h-4 mr-2" />
                    Connect TikTok Account
                  </>
                )}
              </Button>
            </div>
          ) : (
            <>
              {/* Connected Account Info */}
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div className="flex items-center gap-3">
                  {tiktokStatus.account.avatar ? (
                    <img 
                      src={tiktokStatus.account.avatar} 
                      alt="TikTok avatar" 
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                      <TikTokLogo className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-white font-medium">{tiktokStatus.account.display_name}</p>
                    <p className="text-gray-400 text-sm">@{tiktokStatus.account.username}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDisconnect}
                  className="text-gray-400 hover:text-red-400"
                >
                  Disconnect
                </Button>
              </div>

              {/* Publish Status */}
              {publishStatus === "success" && (
                <div className="flex items-center gap-2 p-3 bg-green-900/30 border border-green-800 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <p className="text-green-400">
                    {publishToDrafts ? "Video sent to drafts!" : "Video upload started!"}
                  </p>
                </div>
              )}

              {publishStatus === "error" && (
                <div className="flex items-center gap-2 p-3 bg-red-900/30 border border-red-800 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-500" />
                  <p className="text-red-400">Upload failed. Please try again.</p>
                </div>
              )}

              {/* Caption Input */}
              <div className="space-y-2">
                <Label htmlFor="caption" className="text-gray-300">Caption</Label>
                <Textarea
                  id="caption"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Add a caption for your TikTok..."
                  className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
                  maxLength={2200}
                />
                <p className="text-xs text-gray-500 text-right">{caption.length}/2200</p>
              </div>

              {/* Privacy Setting */}
              <div className="space-y-2">
                <Label className="text-gray-300">Who can view this video</Label>
                <Select value={privacyLevel} onValueChange={setPrivacyLevel}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="PUBLIC_TO_EVERYONE">Everyone</SelectItem>
                    <SelectItem value="FRIEND_ONLY">Friends only</SelectItem>
                    <SelectItem value="SELF_ONLY">Only me</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Save to Drafts Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <Label className="text-gray-300">Save to drafts</Label>
                  <p className="text-xs text-gray-500">Review and post later from TikTok app</p>
                </div>
                <Switch
                  checked={publishToDrafts}
                  onCheckedChange={setPublishToDrafts}
                />
              </div>
            </>
          )}
        </div>

        {tiktokStatus?.connected && (
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-gray-700 text-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              disabled={loading || publishStatus === "success"}
              className="bg-gradient-to-r from-pink-500 to-cyan-500 hover:from-pink-600 hover:to-cyan-600 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : publishStatus === "success" ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Sent!
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  {publishToDrafts ? "Send to Drafts" : "Post to TikTok"}
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default TikTokShareDialog;
